import React, { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  Bug,
  Lightbulb,
  MessageSquare,
  Send,
  Sparkles,
  CheckCircle2,
  Mail,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { submitFeedback } from '../../api/api';

const feedbackTypes = [
  {
    id: 'bug',
    label: 'Bug Report',
    description: 'Something is broken or not working as expected.',
    icon: Bug,
    defaultSubject: 'Bug report',
    helper:
      'Include what happened, what you expected, and how to reproduce it.',
  },
  {
    id: 'feature',
    label: 'Feature Request',
    description: 'Suggest a new capability or improvement.',
    icon: Lightbulb,
    defaultSubject: 'Feature request',
    helper: 'Describe the workflow, pain point, and the ideal solution.',
  },
  {
    id: 'general',
    label: 'General Feedback',
    description: 'Share thoughts about your overall experience.',
    icon: MessageSquare,
    defaultSubject: 'General feedback',
    helper: 'Tell us what feels good, confusing, slow, or missing.',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 18, scale: 0.985 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.06,
      duration: 0.42,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.08 + i * 0.04,
      duration: 0.32,
      ease: 'easeOut',
    },
  }),
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function FeedbackPage({ isDarkMode }) {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const { user } = useSelector((state) => state.auth);

  const token = localStorage.getItem('authToken');
  const isAuthenticated = Boolean(user && token);

  const [type, setType] = useState('feature');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [includeContact, setIncludeContact] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    subject: false,
    message: false,
    email: false,
  });

  const selectedType = useMemo(
    () => feedbackTypes.find((item) => item.id === type),
    [type],
  );

  const finalSubject =
    subject.trim() || selectedType?.defaultSubject || 'Feedback';

  const needsEmail = !isAuthenticated;
  const effectiveEmail = isAuthenticated
    ? includeContact
      ? (user?.email || email || '').trim()
      : ''
    : email.trim();

  const errors = {
    subject:
      touched.subject && finalSubject.trim().length < 3
        ? 'Please add a clearer subject.'
        : '',
    message:
      touched.message && message.trim().length < 12
        ? 'Please provide at least 12 characters.'
        : '',
    email:
      touched.email &&
      needsEmail &&
      (!effectiveEmail
        ? 'Email is required.'
        : !emailRegex.test(effectiveEmail)
          ? 'Enter a valid email address.'
          : '')
        ? touched.email &&
          needsEmail &&
          (!effectiveEmail
            ? 'Email is required.'
            : !emailRegex.test(effectiveEmail)
              ? 'Enter a valid email address.'
              : '')
        : '',
  };

  const isValid =
    finalSubject.trim().length >= 3 &&
    message.trim().length >= 12 &&
    (!needsEmail || (effectiveEmail && emailRegex.test(effectiveEmail)));

  const theme = {
    page: isDarkMode
      ? 'bg-slate-950 text-slate-100'
      : 'bg-[#f7f8fc] text-slate-900',

    subtleText: isDarkMode ? 'text-slate-400' : 'text-slate-600',
    mutedText: isDarkMode ? 'text-slate-500' : 'text-slate-500',

    card: isDarkMode
      ? 'border border-slate-800 bg-slate-900 shadow-[0_16px_50px_rgba(0,0,0,0.30)]'
      : 'border border-slate-200 bg-white shadow-[0_10px_40px_rgba(15,23,42,0.06)]',

    secondaryCard: isDarkMode
      ? 'border border-slate-800 bg-slate-900'
      : 'border border-slate-200 bg-white',

    input: isDarkMode
      ? 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500 focus:border-indigo-400 focus:ring-indigo-500/10'
      : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-indigo-400 focus:ring-indigo-100',

    inputError: isDarkMode
      ? 'border-rose-500/70 focus:border-rose-400 focus:ring-rose-500/10'
      : 'border-rose-300 focus:border-rose-400 focus:ring-rose-100',

    disabledInput: isDarkMode
      ? 'disabled:bg-slate-950 disabled:text-slate-500'
      : 'disabled:bg-slate-50 disabled:text-slate-400',

    backButton: isDarkMode
      ? 'border border-slate-700 bg-slate-900 text-slate-200 hover:border-indigo-500/40 hover:text-indigo-300'
      : 'border border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:text-indigo-600',

    badge: isDarkMode
      ? 'border border-indigo-500/20 bg-indigo-500/10 text-indigo-300'
      : 'border border-indigo-100 bg-indigo-50 text-indigo-700',

    helperPill: isDarkMode
      ? 'border border-slate-700 bg-slate-900 text-slate-300'
      : 'border border-indigo-100 bg-white text-slate-600',

    typeActive: isDarkMode
      ? 'border-indigo-500/40 bg-indigo-500/10 ring-1 ring-indigo-500/20'
      : 'border-indigo-300 bg-indigo-50 ring-1 ring-indigo-100',

    typeIdle: isDarkMode
      ? 'border-slate-700 bg-slate-900 hover:border-slate-600 hover:bg-slate-800/70'
      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',

    iconActive: isDarkMode
      ? 'bg-indigo-500 text-white'
      : 'bg-indigo-600 text-white',

    iconIdle: isDarkMode
      ? 'bg-slate-800 text-slate-300 group-hover:bg-slate-700'
      : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200',

    smallPanel: isDarkMode
      ? 'border border-slate-800 bg-slate-950/60'
      : 'border border-slate-100 bg-slate-50',

    submitButton: isDarkMode
      ? 'bg-indigo-500 text-white hover:bg-indigo-400 shadow-lg shadow-indigo-900/30 disabled:bg-slate-700 disabled:shadow-none'
      : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-200 disabled:bg-slate-300 disabled:shadow-none',
  };

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      subject: true,
      message: true,
      email: true,
    });

    if (!isValid) {
      toast.error('Please fix the highlighted fields.');
      return;
    }

    setSubmitting(true);

    try {
      await submitFeedback({
        subject: finalSubject,
        message: message.trim(),
        email: effectiveEmail,
        type,
      });

      toast.success('Feedback submitted successfully.');
      setSubject('');
      setMessage('');
      if (!isAuthenticated) setEmail('');
      setType('feature');
      setIncludeContact(true);
      setTouched({ subject: false, message: false, email: false });
    } catch (error) {
      toast.error(error.message || 'Could not submit feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen ${theme.page}`}>
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          className="mb-6 flex items-center justify-between"
        >
          <motion.button
            variants={itemVariants}
            custom={0}
            onClick={() => navigate('/', { replace: true })}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium shadow-sm transition ${theme.backButton}`}
          >
            <ArrowLeft size={16} />
            Back
          </motion.button>

          <motion.div
            variants={itemVariants}
            custom={1}
            className={`hidden rounded-full px-4 py-2 text-sm shadow-sm md:flex md:items-center md:gap-2 ${theme.helperPill}`}
          >
            <Sparkles size={16} className="text-indigo-500" />
            Help us improve uLegal.ai
          </motion.div>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.85fr)]">
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            custom={0}
            className={`relative overflow-hidden rounded-3xl p-6 md:p-8 ${theme.card}`}
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.10),transparent_26%),radial-gradient(circle_at_bottom_left,rgba(168,85,247,0.08),transparent_24%)]" />

            <div className="relative">
              <motion.div
                variants={itemVariants}
                custom={0}
                className={`mb-6 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${theme.badge}`}
              >
                <MessageSquare size={16} />
                Feedback & Suggestions
              </motion.div>

              <motion.h1
                variants={itemVariants}
                custom={1}
                className="text-3xl font-semibold tracking-tight md:text-4xl"
              >
                Tell us what would make this better
              </motion.h1>

              <motion.p
                variants={itemVariants}
                custom={2}
                className={`mt-3 max-w-2xl text-sm leading-6 md:text-base ${theme.subtleText}`}
              >
                Share bugs, ideas, or general feedback. Clear, specific details
                help us improve faster.
              </motion.p>

              <motion.div
                variants={itemVariants}
                custom={3}
                className="mt-8 grid gap-3 sm:grid-cols-3"
              >
                {feedbackTypes.map((item) => {
                  const Icon = item.icon;
                  const active = type === item.id;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setType(item.id)}
                      aria-pressed={active}
                      className={[
                        'group rounded-2xl border p-4 text-left transition-all duration-200',
                        active ? theme.typeActive : theme.typeIdle,
                      ].join(' ')}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={[
                            'mt-0.5 rounded-xl p-2 transition',
                            active ? theme.iconActive : theme.iconIdle,
                          ].join(' ')}
                        >
                          <Icon size={16} />
                        </div>

                        <div>
                          <p className="text-sm font-semibold">{item.label}</p>
                          <p
                            className={`mt-1 text-xs leading-5 ${theme.mutedText}`}
                          >
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </motion.div>

              <motion.div
                variants={itemVariants}
                custom={4}
                className={`mt-5 rounded-2xl p-4 ${theme.smallPanel}`}
              >
                <p className="text-sm font-semibold">{selectedType?.label}</p>
                <p className={`mt-1 text-sm leading-6 ${theme.subtleText}`}>
                  {selectedType?.helper}
                </p>
              </motion.div>

              <form
                onSubmit={handleSubmit}
                className="mt-8 space-y-5"
                noValidate
              >
                <motion.div variants={itemVariants} custom={5}>
                  <label
                    htmlFor="subject"
                    className="mb-2 block text-sm font-medium"
                  >
                    Subject
                  </label>
                  <div className="relative">
                    <FileText
                      size={16}
                      className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${theme.mutedText}`}
                    />
                    <input
                      id="subject"
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      onBlur={() => markTouched('subject')}
                      placeholder={`e.g. ${selectedType?.defaultSubject}`}
                      aria-invalid={Boolean(errors.subject)}
                      aria-describedby={
                        errors.subject ? 'subject-error' : 'subject-help'
                      }
                      className={`w-full rounded-2xl border py-3 pl-11 pr-4 text-sm outline-none transition focus:ring-4 ${theme.input} ${errors.subject ? theme.inputError : ''}`}
                      maxLength={120}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span id="subject-help" className={theme.mutedText}>
                      Leave blank to use “{selectedType?.defaultSubject}”.
                    </span>
                    <span className={theme.mutedText}>
                      {subject.length}/120
                    </span>
                  </div>
                  {errors.subject && (
                    <p
                      id="subject-error"
                      className="mt-2 text-xs text-rose-500"
                    >
                      {errors.subject}
                    </p>
                  )}
                </motion.div>

                <motion.div variants={itemVariants} custom={6}>
                  <label
                    htmlFor="message"
                    className="mb-2 block text-sm font-medium"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onBlur={() => markTouched('message')}
                    rows={8}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={
                      errors.message ? 'message-error' : 'message-help'
                    }
                    placeholder={
                      type === 'bug'
                        ? 'Describe what happened, what you expected, and how to reproduce it.'
                        : type === 'feature'
                          ? 'Describe the improvement or workflow you would like to see.'
                          : 'Tell us about your experience using the product.'
                    }
                    className={`w-full resize-none rounded-2xl border px-4 py-3 text-sm outline-none transition focus:ring-4 ${theme.input} ${errors.message ? theme.inputError : ''}`}
                    maxLength={2000}
                  />
                  <div
                    className={`mt-2 flex items-center justify-between text-xs ${theme.mutedText}`}
                  >
                    <span id="message-help">
                      Be specific about impact and expected outcome.
                    </span>
                    <span>{message.length}/2000</span>
                  </div>
                  {errors.message && (
                    <p
                      id="message-error"
                      className="mt-2 text-xs text-rose-500"
                    >
                      {errors.message}
                    </p>
                  )}
                </motion.div>

                <motion.div variants={itemVariants} custom={7}>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-medium"
                  >
                    Contact email
                  </label>

                  <div className="relative">
                    <Mail
                      size={16}
                      className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${theme.mutedText}`}
                    />
                    <input
                      id="email"
                      type="email"
                      value={isAuthenticated ? user?.email || email : email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => markTouched('email')}
                      disabled={isAuthenticated && Boolean(user?.email)}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={
                        errors.email ? 'email-error' : 'email-help'
                      }
                      placeholder={
                        needsEmail
                          ? 'Email is required'
                          : user?.email || 'name@company.com'
                      }
                      className={`w-full rounded-2xl border py-3 pl-11 pr-4 text-sm outline-none transition focus:ring-4 ${theme.input} ${theme.disabledInput} ${errors.email ? theme.inputError : ''}`}
                    />
                  </div>

                  {/* {isAuthenticated && (
                    <label className="mt-3 flex items-start gap-3 text-sm">
                      <input
                        type="checkbox"
                        checked={includeContact}
                        onChange={(e) => setIncludeContact(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-slate-300"
                      />
                      <span className={theme.subtleText}>
                        Allow follow-up by email
                        {user?.email ? ` (${user.email})` : ''}.
                      </span>
                    </label>
                  )} */}

                  <p
                    id="email-help"
                    className={`mt-2 text-xs ${
                      needsEmail ? 'text-red-500' : theme.mutedText
                    }`}
                  >
                    {needsEmail
                      ? 'No valid authenticated session was found, so email is required.'
                      : includeContact
                        ? 'We may contact you if we need more detail.'
                        : 'Your feedback will still be submitted without a contact email.'}
                  </p>

                  {errors.email && (
                    <p id="email-error" className="mt-2 text-xs text-rose-500">
                      {errors.email}
                    </p>
                  )}
                </motion.div>

                <motion.div
                  variants={itemVariants}
                  custom={8}
                  className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div
                    className={`flex items-center gap-2 text-sm ${theme.subtleText}`}
                  >
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    Clear feedback helps prioritize product improvements.
                  </div>

                  <motion.button
                    whileHover={
                      prefersReducedMotion ? {} : { y: -1, scale: 1.01 }
                    }
                    whileTap={prefersReducedMotion ? {} : { scale: 0.985 }}
                    type="submit"
                    disabled={submitting}
                    className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition ${theme.submitButton}`}
                  >
                    {submitting ? (
                      <>
                        <motion.span
                          animate={{ rotate: 360 }}
                          transition={{
                            repeat: Infinity,
                            duration: 1,
                            ease: 'linear',
                          }}
                          className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                        />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Submit Feedback
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </form>
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            custom={1}
            className="space-y-6 lg:sticky lg:top-6 lg:self-start"
          >
            <div className={`rounded-3xl p-6 ${theme.secondaryCard}`}>
              <h2 className="text-lg font-semibold">
                What happens after you submit?
              </h2>

              <div className="mt-5 space-y-4">
                {[
                  {
                    title: 'We review every submission',
                    text: 'Feedback is categorized and shared with the product team.',
                  },
                  {
                    title: 'High-impact issues are prioritized',
                    text: 'Bugs and workflow blockers get faster attention.',
                  },
                  {
                    title: 'Feature requests shape the roadmap',
                    text: 'Repeated requests help us decide what to build next.',
                  },
                ].map((step, i) => (
                  <motion.div
                    key={step.title}
                    initial={
                      prefersReducedMotion ? false : { opacity: 0, x: 12 }
                    }
                    animate={prefersReducedMotion ? {} : { opacity: 1, x: 0 }}
                    transition={{ delay: 0.18 + i * 0.08, duration: 0.35 }}
                    className={`rounded-2xl p-4 ${theme.smallPanel}`}
                  >
                    <p className="text-sm font-semibold">{step.title}</p>
                    <p className={`mt-1 text-sm leading-6 ${theme.subtleText}`}>
                      {step.text}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-indigo-950 p-6 text-white shadow-[0_10px_40px_rgba(15,23,42,0.12)]">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 p-3">
                  <Sparkles size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    Tips for better submissions
                  </h3>
                  <p className="text-sm text-white/75">
                    The more concrete the report, the easier it is to act on.
                  </p>
                </div>
              </div>

              <div className="mt-5 space-y-3 text-sm text-white/85">
                <div className="rounded-2xl bg-white/10 p-4">
                  What were you trying to do?
                </div>
                <div className="rounded-2xl bg-white/10 p-4">
                  What happened instead?
                </div>
                <div className="rounded-2xl bg-white/10 p-4">
                  What outcome would feel better?
                </div>
              </div>
            </div>

            <div className={`rounded-3xl p-6 ${theme.secondaryCard}`}>
              <div className="flex items-start gap-3">
                <div className={`rounded-2xl p-3 ${theme.smallPanel}`}>
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <h3 className="text-base font-semibold">Privacy note</h3>
                  <p className={`mt-1 text-sm leading-6 ${theme.subtleText}`}>
                    Only include account details or contact info you’re
                    comfortable sharing.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
